// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, input, output, computed, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent } from '@lfx/shared/components/button';

export interface BaseModalConfig {
  title: string;
  size?: 'sm' | 'lg' | 'xl';
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'secondary' | 'danger';
  showCancel?: boolean;
  showConfirm?: boolean;
  confirmDisabled?: boolean;
  allowBackdropClose?: boolean;
}

@Component({
  selector: 'lfx-base-modal',
  imports: [CommonModule, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="modal-header">
      <h4 class="modal-title" id="modal-basic-title">{{ config().title }}</h4>
      @if (shouldShowCloseButton()) {
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          (click)="onCancel()">
        </button>
      }
    </div>

    <div class="modal-body">
      <!-- Error Message -->
      @if (currentErrorMessage()) {
        <div class="alert alert-danger alert-dismissible mb-3" role="alert">
          <small>{{ currentErrorMessage() }}</small>
        </div>
      }

      <!-- Content Projection -->
      <ng-content></ng-content>
    </div>

    <div class="modal-footer">
      @if (shouldShowCancel()) {
        <lfx-button
          variant="tertiary"
          size="large"
          type="button"
          [disabled]="currentIsLoading()"
          (clicked)="onCancel()">
          {{ cancelLabel() }}
        </lfx-button>
      }

      @if (shouldShowConfirm()) {
        <lfx-button
          [variant]="confirmVariant()"
          size="large"
          type="button"
          [disabled]="isConfirmDisabled()"
          [loading]="currentIsLoading()"
          (clicked)="onConfirm()">
          {{ confirmLabel() }}
        </lfx-button>
      }
    </div>
  `,
  styleUrls: ['./base-modal.component.scss']
})
export class BaseModalComponent {
  // Modern input/output patterns
  readonly config = input.required<BaseModalConfig>();
  readonly isLoading = input<boolean>(false);
  readonly errorMessage = input<string>('');

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();

  // Internal state management for child components
  private readonly internalIsLoading = signal<boolean>(false);
  private readonly internalErrorMessage = signal<string>('');

  // Dependency injection using inject()
  private readonly activeModal = inject(NgbActiveModal);

  // Computed properties for derived state
  protected readonly shouldShowCloseButton = computed(() =>
    this.config().allowBackdropClose !== false
  );

  protected readonly shouldShowCancel = computed(() =>
    this.config().showCancel !== false
  );

  protected readonly shouldShowConfirm = computed(() =>
    this.config().showConfirm !== false
  );

  protected readonly cancelLabel = computed(() =>
    this.config().cancelLabel || 'Cancel'
  );

  protected readonly confirmLabel = computed(() =>
    this.config().confirmLabel || 'Confirm'
  );

  protected readonly confirmVariant = computed(() =>
    this.config().confirmVariant || 'primary'
  );

  // Combine external and internal state for loading and error
  protected readonly currentIsLoading = computed(() =>
    this.isLoading() || this.internalIsLoading()
  );

  protected readonly currentErrorMessage = computed(() =>
    this.errorMessage() || this.internalErrorMessage()
  );

  protected readonly isConfirmDisabled = computed(() =>
    this.config().confirmDisabled || this.currentIsLoading()
  );

  protected onConfirm(): void {
    this.confirmed.emit();
  }

  protected onCancel(): void {
    this.cancelled.emit();
    this.activeModal.dismiss('cancel');
  }

  // Helper methods for child components
  closeWithResult(result: any): void {
    this.activeModal.close(result);
  }

  setLoading(loading: boolean): void {
    this.internalIsLoading.set(loading);
  }

  setError(message: string): void {
    this.internalErrorMessage.set(message);
  }

  clearError(): void {
    this.internalErrorMessage.set('');
  }
}
